import {
  BMButton,
  BMCard,
  BMInputField,
  BMNumberStepper,
  BMPrimitives,
  BMText,
} from "@traveloka/district-bloom";
import IcHotelRoomGuest from "@traveloka/icon-kit-web/react/IcHotelRoomGuest";
import IcSystemCalendarIn from "@traveloka/icon-kit-web/react/IcSystemCalendarIn";
import IcSystemCalendarOut from "@traveloka/icon-kit-web/react/IcSystemCalendarOut";
import IcSystemLocationBloom from "@traveloka/icon-kit-web/react/IcSystemLocationBloom";
import IcSystemSearch from "@traveloka/icon-kit-web/react/IcSystemSearch";
import { FieldErrors, useFormContext, useWatch } from "react-hook-form";
import { StyleSheet } from "react-native";
import BMView from "../../../components/View/BMView";
import { useMobileContentResource } from "../../../core/resource/MobileResourceProvider";
import { formatMessage } from "../../../utils";
import { HotelSearchFormErrors, HotelSearchFormValues } from "./types";

type Props = {
  onSearchFocus: () => void;
  onCalendarFocus: () => void;
  onTravelerFocus: () => void;
  onSubmitForm: (values: HotelSearchFormValues) => void;
  onErrorForm: (errors: FieldErrors<HotelSearchFormValues>) => void;
  formErrors: HotelSearchFormErrors;
};

const MAX_ROOMS = 8;

export default function SearchFormCard(props: Props) {
  const {
    onSearchFocus,
    onCalendarFocus,
    onTravelerFocus,
    onSubmitForm,
    onErrorForm,
    formErrors,
  } = props;
  const form = useFormContext<HotelSearchFormValues>();

  const [rooms, geoName] = useWatch({
    control: form.control,
    name: ["rooms", "geoName"],
  });

  const { CorporateProductSearchForm, CorporateHotelSearchForm } =
    useMobileContentResource();

  function guestText() {
    const values = form.getValues();
    const { nonEmployeeTravelers, travelers, employeeName } = values;
    if (
      travelers.length === 1 &&
      !nonEmployeeTravelers.adults &&
      !nonEmployeeTravelers.childrenAges.length
    ) {
      return employeeName;
    }

    const parts: string[] = [];

    if (nonEmployeeTravelers.adults || travelers.length) {
      parts.push(
        formatMessage(CorporateProductSearchForm.nonEmployeeAdultUnit, {
          num: nonEmployeeTravelers.adults + travelers.length,
        })
      );
    }

    if (nonEmployeeTravelers.childrenAges.length > 0) {
      parts.push(
        formatMessage(CorporateProductSearchForm.nonEmployeeChildUnit, {
          num: nonEmployeeTravelers.childrenAges.length,
        })
      );
    }

    return parts.join(", ");
  }

  const MAX_ROOMS_ALLOWED = Math.min(
    MAX_ROOMS,
    form.getValues("travelers").length +
      form.getValues("nonEmployeeTravelers").adults
  );

  return (
    <BMView align="center" style={styles.container}>
      <BMCard style={styles.card} layout="COMPACT" elevation="FLOAT">
        <BMText variant="titleSmall" ink="BMStyleTextInkPrimary">
          {CorporateHotelSearchForm.bookYourHotel}
        </BMText>
        <BMView style={styles.formSection}>
          <BMInputField
            editable={false}
            onFocus={onSearchFocus}
            LeadingIcon={IcSystemLocationBloom}
            placeholder={CorporateHotelSearchForm.destinationPlaceholder}
            label={CorporateHotelSearchForm.destination}
            value={geoName}
            error={formErrors.destination}
          />
        </BMView>
        <BMView flex row justify="between" style={styles.formSection}>
          <BMView style={styles.fieldContainer}>
            <BMInputField
              editable={false}
              onFocus={onCalendarFocus}
              LeadingIcon={IcSystemCalendarIn}
              label={CorporateHotelSearchForm.checkInLabel}
              value={form.getValues("checkInDate")}
            />
          </BMView>
          <BMView style={styles.fieldContainer}>
            <BMInputField
              editable={false}
              onFocus={onCalendarFocus}
              LeadingIcon={IcSystemCalendarOut}
              label={CorporateHotelSearchForm.checkOutLabel}
              value={form.getValues("checkOutDate")}
            />
          </BMView>
        </BMView>
        <BMView
          flex
          row
          align="center"
          justify="between"
          style={styles.formSection}
        >
          <BMView style={styles.fieldContainer}>
            <BMInputField
              editable={false}
              LeadingIcon={IcHotelRoomGuest}
              placeholder={CorporateHotelSearchForm.emptyGuestText}
              label={CorporateHotelSearchForm.travelerLabel}
              onFocus={onTravelerFocus}
              value={guestText() || ""}
              error={formErrors.guest}
            />
          </BMView>
          <BMView style={styles.fieldContainer}>
            <BMText
              style={styles.stepperLabel}
              variant="uiNormal"
              ink="BMStyleTextInkPrimary"
            >
              {CorporateHotelSearchForm.roomLabel}
            </BMText>
            <BMNumberStepper
              minValue={1}
              maxValue={MAX_ROOMS_ALLOWED}
              value={rooms}
              onChange={(value) => form.setValue("rooms", value)}
            />
          </BMView>
        </BMView>
        <BMView style={styles.formSection}>
          <BMButton
            label={CorporateHotelSearchForm.searchButtonText}
            onPress={form.handleSubmit(onSubmitForm, onErrorForm)}
            size="LARGE"
            stretchable
            TrailingIcon={IcSystemSearch}
            iconStyle={{
              color: "#fff",
            }}
          />
        </BMView>
      </BMCard>
    </BMView>
  );
}

const styles = StyleSheet.create({
  container: {
    width: "100%",
    height: 344,
    paddingHorizontal: BMPrimitives.BMSize.md,
  },
  card: {
    width: "100%",
    height: "100%",
  },
  fieldContainer: {
    width: "47%",
    height: 62,
  },
  formSection: {
    marginTop: BMPrimitives.BMSize.lg,
  },
  stepperLabel: {
    marginBottom: BMPrimitives.BMSize.xs,
  },
});
